import type { DraftMessageId } from "@redotech/effect-api-shared/ids/definitions";
import type { DraftMessageStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { DraftSendStage } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-notification";

/**
 * Optimistic patch applied to a draft collection item as it moves through the
 * send lifecycle. `status` is the coarse persisted-shape state used by show/hide
 * logic; `sendStage`/`sendAt` are transient client-only refinements that drive
 * the displayed label ("Waiting…" vs "Sending…" vs "Failed") and the undo
 * countdown. `sendStage`/`sendAt` are always set explicitly (including to
 * `undefined`) so a patch fully replaces any prior in-flight state.
 */
export interface DraftSendStatusPatch {
  id: DraftMessageId;
  status: DraftMessageStatus;
  sendStage: DraftSendStage | undefined;
  sendAt: string | undefined;
}

/**
 * Enter the undo window. `sendAt` (from the `saveDraftAndSend` response) is the
 * scheduled dispatch time; the composer drives its countdown from it.
 */
export function markDraftWaiting(
  draftId: DraftMessageId,
  sendAt: string,
): DraftSendStatusPatch {
  return { id: draftId, status: "SENDING", sendStage: "waiting", sendAt };
}

/**
 * Undo window elapsed; the message is being dispatched. Driven by the
 * `draftSendStateChanged` SSE (and derivable client-side once now ≥ sendAt).
 */
export function markDraftSendingStage(
  draftId: DraftMessageId,
  sendAt: string | undefined,
): DraftSendStatusPatch {
  return { id: draftId, status: "SENDING", sendStage: "sending", sendAt };
}

/** Terminal send failure — surface a retry affordance instead of vanishing. */
export function markDraftSendError(
  draftId: DraftMessageId,
): DraftSendStatusPatch {
  return {
    id: draftId,
    status: "ERROR",
    sendStage: "error",
    sendAt: undefined,
  };
}

/**
 * Mark a draft as in-flight before we know the server `sendAt` (the optimistic
 * write fired the instant the user clicks send, before the RPC resolves).
 */
export function markDraftSending(
  draftId: DraftMessageId,
): DraftSendStatusPatch {
  return {
    id: draftId,
    status: "SENDING",
    sendStage: "waiting",
    sendAt: undefined,
  };
}

/** Roll back to an editable draft (undo, or failed pre-dispatch). */
export function restoreDraftPending(
  draftId: DraftMessageId,
): DraftSendStatusPatch {
  return {
    id: draftId,
    status: "PENDING",
    sendStage: undefined,
    sendAt: undefined,
  };
}
